/** Role-aware company nav — plan/15, plan/27. Capacitor deferred. */

import { hasActionPermission } from "./permission-client";

export type CompanyNavId =
  | "home"
  | "pos"
  | "sales"
  | "contacts"
  | "returns"
  | "purchasing"
  | "products"
  | "inventory"
  | "warehouses"
  | "convert"
  | "trialBalance"
  | "profitLoss"
  | "chartOfAccounts"
  | "approvals"
  | "inbox"
  | "audit"
  | "orgSettings"
  | "payMethods"
  | "priceLists"
  | "docSeries"
  | "branches"
  | "branchAccess";

export type CompanyNavItem = {
  id: CompanyNavId;
  label: string;
  path: (slug: string) => string;
  primary?: boolean;
  /** Lives in shell chrome (bell), not the module nav row. */
  chromeOnly?: boolean;
};

const ITEMS: CompanyNavItem[] = [
  {
    id: "home",
    label: "Home",
    path: (s) => `/c/${s}`,
    primary: true,
  },
  {
    id: "pos",
    label: "POS",
    path: (s) => `/c/${s}/pos/tickets`,
    primary: true,
  },
  {
    id: "sales",
    label: "Sales",
    path: (s) => `/c/${s}/sales/orders`,
    primary: true,
  },
  {
    id: "contacts",
    label: "Contacts",
    path: (s) => `/c/${s}/contacts`,
    primary: true,
  },
  {
    id: "returns",
    label: "Returns",
    path: (s) => `/c/${s}/returns`,
    primary: true,
  },
  {
    id: "purchasing",
    label: "Purchasing",
    path: (s) => `/c/${s}/purchasing/orders`,
    primary: true,
  },
  {
    id: "products",
    label: "Products",
    path: (s) => `/c/${s}/products`,
    primary: true,
  },
  {
    id: "inventory",
    label: "Inventory",
    path: (s) => `/c/${s}/inventory`,
    primary: true,
  },
  {
    id: "warehouses",
    label: "Warehouses",
    path: (s) => `/c/${s}/warehouses`,
  },
  {
    id: "convert",
    label: "Convert",
    path: (s) => `/c/${s}/inventory/convert`,
    primary: true,
  },
  {
    id: "trialBalance",
    label: "Trial balance",
    path: (s) => `/c/${s}/gl/trial-balance`,
    primary: true,
  },
  {
    id: "profitLoss",
    label: "Profit & loss",
    path: (s) => `/c/${s}/gl/profit-loss`,
  },
  {
    id: "chartOfAccounts",
    label: "Chart of accounts",
    path: (s) => `/c/${s}/gl/accounts`,
  },
  {
    id: "approvals",
    label: "Approvals",
    path: (s) => `/c/${s}/approvals`,
  },
  {
    id: "inbox",
    label: "Inbox",
    path: (s) => `/c/${s}/inbox`,
    /** Chrome bell only — not primary/More (plan/50). */
    chromeOnly: true,
  },
  {
    id: "orgSettings",
    label: "Org settings",
    path: (s) => `/c/${s}/settings/org`,
  },
  {
    id: "payMethods",
    label: "Pay methods",
    path: (s) => `/c/${s}/settings/pay-methods`,
  },
  {
    id: "priceLists",
    label: "Price lists",
    path: (s) => `/c/${s}/settings/price-lists`,
  },
  {
    id: "docSeries",
    label: "Document numbers",
    path: (s) => `/c/${s}/settings/doc-series`,
  },
  {
    id: "branches",
    label: "Branches",
    path: (s) => `/c/${s}/settings/branches`,
  },
  {
    id: "branchAccess",
    label: "Branch access",
    path: (s) => `/c/${s}/settings/branches/access`,
  },
  {
    id: "audit",
    label: "Audit",
    path: (s) => `/c/${s}/audit`,
  },
];

export function canSeeCompanyNav(
  held: readonly string[],
  id: CompanyNavId,
): boolean {
  if (hasActionPermission(held, "*")) return true;
  switch (id) {
    case "home":
      // Any signed-in company member — shop hub / widgets
      return held.length > 0;
    case "pos":
      return (
        hasActionPermission(held, "pos.sell") ||
        hasActionPermission(held, "pos.open_terminal")
      );
    case "sales":
      return (
        hasActionPermission(held, "sales.read") ||
        hasActionPermission(held, "sales.write")
      );
    case "contacts":
      return (
        hasActionPermission(held, "sales.read") ||
        hasActionPermission(held, "sales.write") ||
        hasActionPermission(held, "purchasing.read") ||
        hasActionPermission(held, "purchasing.write")
      );
    case "returns":
      return (
        hasActionPermission(held, "pos.return") ||
        hasActionPermission(held, "sales.write")
      );
    case "purchasing":
      return (
        hasActionPermission(held, "purchasing.read") ||
        hasActionPermission(held, "purchasing.write")
      );
    case "products":
    case "inventory":
    case "warehouses":
    case "convert":
      return (
        hasActionPermission(held, "inventory.read") ||
        hasActionPermission(held, "inventory.write")
      );
    case "trialBalance":
    case "profitLoss":
    case "chartOfAccounts":
      return (
        hasActionPermission(held, "gl.read") ||
        hasActionPermission(held, "gl.write")
      );
    case "approvals":
      return hasActionPermission(held, "can_approve");
    case "inbox":
      // Any signed-in member — mentions are for everyone on the floor
      return held.length > 0;
    case "orgSettings":
    case "payMethods":
    case "priceLists":
    case "docSeries":
    case "branches":
    case "branchAccess":
      return hasActionPermission(held, "settings.users");
    case "audit":
      return (
        hasActionPermission(held, "gl.read") ||
        hasActionPermission(held, "settings.users")
      );
    default:
      return false;
  }
}

export function companyNavForPermissions(held: readonly string[]): {
  primary: CompanyNavItem[];
  more: CompanyNavItem[];
} {
  const visible = ITEMS.filter(
    (item) => canSeeCompanyNav(held, item.id) && !item.chromeOnly,
  );
  return {
    primary: visible.filter((i) => i.primary),
    more: visible.filter((i) => !i.primary),
  };
}

export function shopDayHint(held: readonly string[]): string | null {
  // Owner / people-admin: seed defaults cover cashiers + first path — no setup checklist.
  if (
    hasActionPermission(held, "*") ||
    hasActionPermission(held, "settings.users")
  ) {
    return null;
  }
  if (hasActionPermission(held, "pos.sell") && !hasActionPermission(held, "sales.write")) {
    return "Manager opens the terminal with Google, then sign in with your user code + PIN.";
  }
  if (hasActionPermission(held, "gl.read")) {
    return "Check trial balance after sales. Opening cash needs Owner / Accountant.";
  }
  if (hasActionPermission(held, "purchasing.write") || hasActionPermission(held, "inventory.write")) {
    return "Products → Purchasing (order → receive → pay) → Convert packs if needed.";
  }
  return null;
}
